import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  InputAdornment,
  Pagination,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import { useAnswersQuery } from "../../queries/useToeicQueries";

const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "999px",
    bgcolor: "var(--toeic-surface)",
    boxShadow: "none",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputAdornment-root": {
    width: 30,
    height: 30,
    maxHeight: 30,
    borderRadius: "999px",
    bgcolor: "#e8f6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    mt: "0 !important",
    mr: 0.75,
  },
  "& .MuiOutlinedInput-input": { py: 0, fontWeight: 500, fontSize: 14 },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

const examAccents = [
  { bg: "var(--toeic-coral)", color: "#ffffff", soft: "rgba(233, 109, 120, 0.12)" },
  { bg: "var(--toeic-sky)", color: "#ffffff", soft: "rgba(70, 168, 232, 0.12)" },
  { bg: "var(--toeic-mint)", color: "#ffffff", soft: "rgba(79, 191, 159, 0.13)" },
  { bg: "var(--toeic-gold)", color: "var(--toeic-ink)", soft: "rgba(240, 184, 77, 0.16)" },
];

const getExamAccent = (index) => examAccents[index % examAccents.length];

function Test() {
  const [keySearch, setKeySearch] = React.useState("");
  const [page, setPage] = useState(1);
  const itemsPerPage = 12;
  const { data: content = [] } = useAnswersQuery(keySearch);

  const handleSearchChange = (e) => {
    setKeySearch(e.target.value);
    setPage(1);
  };

  const indexOfLastItem = page * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = content.slice(indexOfFirstItem, indexOfLastItem);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        mb: 6,
      }}
    >
      <Box component="header">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "end",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box className="toeic-reveal">
            <Typography
              component="h1"
              sx={{
                m: 0,
                color: "var(--toeic-ink)",
                fontSize: { xs: 36, md: 56 },
                lineHeight: { xs: 1.05, md: 1 },
                fontWeight: 800,
                letterSpacing: 0,
              }}
            >
              <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    left: "-0.12em",
                    right: "-0.12em",
                    bottom: "0.04em",
                    height: "0.44em",
                    bgcolor: "rgba(79, 191, 159, 0.26)",
                    borderRadius: "0.45em 0.6em 0.4em 0.55em",
                    transform: "rotate(-1deg)",
                  }}
                />
                <Box component="span" sx={{ position: "relative" }}>
                  TOEIC Tests
                </Box>
              </Box>
              .
            </Typography>
          </Box>
        </Box>
        <Typography
          className="toeic-reveal"
          sx={{
            mt: 2,
            maxWidth: 680,
            color: "var(--toeic-ink-soft)",
            fontSize: 18,
            lineHeight: 1.65,
            fontWeight: 700,
          }}
        >
          Pick a TOEIC practice set, start the answer sheet, and keep the list easy to scan.
        </Typography>
      </Box>

      <Box sx={{ mt: 4, display: "grid", gap: 4 }}>
        <Box
          aria-hidden="true"
          sx={{
            borderTop: "1.5px dotted var(--toeic-line)",
            width: "100%",
          }}
        />

        <Box
          className="toeic-reveal"
        sx={{
          p: isMobile ? 2 : 2.5,
          borderRadius: "18px",
          border: "1px solid rgba(215, 234, 245, 0.82)",
          bgcolor: "var(--toeic-paper-grid)",
          boxShadow: "var(--toeic-shadow-card)",
          display: "grid",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: "var(--toeic-ink)",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".12em",
            }}
          >
            Test Filter
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 700 }}>
            {content.length} tests - 75m mode
          </Typography>
        </Box>

        <TextField
          label="Search Test"
          variant="outlined"
          fullWidth
          value={keySearch}
          onChange={handleSearchChange}
          sx={searchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "var(--toeic-sky-deep)", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        </Box>

        <Box
          aria-hidden="true"
          sx={{
            borderTop: "1.5px dotted var(--toeic-line)",
            width: "100%",
          }}
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 4 }}>
        {currentItems.length > 0 ? (
          currentItems.map((item, index) => {
            const order = indexOfFirstItem + index + 1;
            const accent = getExamAccent(order - 1);

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: "var(--toeic-radius-md)",
                    border: "1px solid rgba(215, 234, 245, 0.72)",
                    bgcolor: "var(--toeic-surface)",
                    boxShadow: "var(--toeic-shadow-card)",
                    transition: "transform 0.22s var(--toeic-ease-fluid), box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 24px 44px -30px rgba(19, 43, 63, 0.52)",
                    },
                  }}
                >
                  <CardActionArea
                    component={Link}
                    to={`/answersheet/${item._id}`}
                    sx={{
                      height: "100%",
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      gap: 2,
                      "&:hover .exam-card-arrow": {
                        color: accent.bg,
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "14px",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: accent.bg,
                          color: accent.color,
                          fontWeight: 900,
                          fontSize: 22,
                          lineHeight: 1,
                        }}
                      >
                        {String(order).padStart(2, "0")}
                      </Box>
                      <Chip
                        label="Free"
                        size="small"
                        sx={{
                          bgcolor: accent.soft,
                          color: accent.bg,
                          fontWeight: 700,
                        }}
                      />
                    </Box>
                    <Box>
                      <Typography
                        component="h3"
                        sx={{
                          color: "var(--toeic-ink)",
                          fontSize: 18,
                          fontWeight: 900,
                          lineHeight: 1.25,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.answer_name}
                      </Typography>
                      <Typography sx={{ mt: 0.75, color: "var(--toeic-ink-soft)", fontSize: 13, fontWeight: 600 }}>
                        200 questions - listening + reading
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        mt: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ color: "var(--toeic-ink)", fontSize: 14, fontWeight: 700 }}>
                        Start practice
                      </Typography>
                      <ArrowForwardIcon
                        className="exam-card-arrow"
                        sx={{
                          color: "var(--toeic-ink-soft)",
                          fontSize: 18,
                          flex: "0 0 auto",
                          transition: "transform 180ms ease, color 180ms ease",
                        }}
                      />
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12}>
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: "18px",
              border: "1.5px dashed var(--toeic-line)",
              bgcolor: "var(--toeic-surface)",
            }}
          >
          <Typography sx={{ color: "var(--toeic-ink-soft)", p: 2, fontWeight: 800 }}>
            No test available
          </Typography>
          </Box>
          </Grid>
        )}
      </Grid>

        <Pagination
          count={Math.ceil(content.length / itemsPerPage)}
          page={page}
          onChange={handleChangePage}
          sx={{ mt: 3, display: "flex", justifyContent: "center" }}
        />
    </Box>
  );
}

export default Test;
